import { Link } from "react-router";
import { Adjuntos, Alerta, Badge, Button, Casilla, Combobox, DataList, Dialog, MetricCard, PercentField, Row, Select, SelectorArchivos, Spinner, Stack, TextField } from "@/components";
import type { FeeGenerado, LiquidacionFee } from "@/lib/api";
import { ACCEPT_ADJUNTO } from "@/lib/adjuntos";
import { formatFecha, formatMoneda, formatNumero } from "@/lib/format";
import {
  ETIQUETA_ESTADO_FEE, NOMBRE_MONEDA, OPCIONES_FILTRO_ESTADO_FEE, OPCIONES_FILTRO_TIPO_FEE, TONO_ESTADO_FEE, nombreMes, textoEstadoFee, textoTotales,
  type LineaBeneficiario,
} from "@/lib/honorarios";
import { useHonorariosTab } from "./useHonorariosTab";

export function HonorariosTab() {
  const t = useHonorariosTab();
  const h = t.honorarios;

  const acreditado = (l: LineaBeneficiario) => h?.beneficiarios.find((b) => b.fiduciante_id === l.fiduciante_id);
  const columnasB = [
    { clave: "nombre", titulo: "Fiduciante", render: (l: LineaBeneficiario) => l.nombre },
    {
      clave: "pct",
      titulo: "%",
      render: (l: LineaBeneficiario) => <PercentField etiqueta={`% de ${l.nombre}`} valor={l.porcentaje} onCambio={(v) => t.cambiarPorcentaje(l.fiduciante_id, v)} />,
    },
    {
      clave: "acreditado",
      titulo: "Acreditado",
      render: (l: LineaBeneficiario) => {
        const b = acreditado(l);
        if (!b) return "—";
        const partes = [Number(b.acreditado_ars) ? formatMoneda(b.acreditado_ars, "ARS") : null, Number(b.acreditado_usd) ? formatMoneda(b.acreditado_usd, "USD") : null].filter(Boolean);
        return partes.length ? partes.join(" + ") : formatMoneda("0", "ARS");
      },
    },
  ];

  const columnasLiq = [
    { clave: "mes", titulo: "Mes", render: (l: LiquidacionFee) => nombreMes(l.mes) },
    { clave: "factura", titulo: "Factura", render: (l: LiquidacionFee) => l.factura_nro },
    { clave: "fecha", titulo: "Fecha", render: (l: LiquidacionFee) => formatFecha(l.fecha) },
    {
      clave: "monto",
      titulo: "Monto",
      render: (l: LiquidacionFee) => (l.tipo_cambio ? `${formatMoneda(l.monto, l.moneda)} (TC ${formatNumero(l.tipo_cambio)})` : formatMoneda(l.monto, l.moneda)),
    },
    { clave: "fees", titulo: "Fees", render: (l: LiquidacionFee) => String(l.cantidad_fees) },
    { clave: "caja", titulo: "Caja", render: (l: LiquidacionFee) => l.caja },
  ];

  const columnasFees = [
    {
      clave: "elegir",
      titulo: "Liquidar",
      render: (i: FeeGenerado) =>
        i.tipo === "A" && i.estado === "por_cobrar" ? (
          <Casilla etiqueta={`Elegir ${i.descripcion}`} etiquetaOculta marcado={t.elegido(i)} onCambio={() => t.alternar(i)} deshabilitado={!t.puedeElegir(i)} />
        ) : null,
    },
    { clave: "fecha", titulo: "Fecha", render: (i: FeeGenerado) => formatFecha(i.fecha) },
    {
      clave: "gasto",
      titulo: "Gasto",
      render: (i: FeeGenerado) => (
        <Link to={t.rutaGasto(i)} className="font-semibold text-primario underline-offset-2 hover:underline">
          {i.descripcion}
        </Link>
      ),
    },
    { clave: "proveedor", titulo: "Proveedor", render: (i: FeeGenerado) => i.proveedor ?? "—" },
    { clave: "monto", titulo: "Monto del gasto", render: (i: FeeGenerado) => formatMoneda(i.monto_gasto, i.moneda) },
    { clave: "pct", titulo: "%", render: (i: FeeGenerado) => (i.porcentaje ? `${formatNumero(i.porcentaje)} %` : "—") },
    {
      clave: "fee",
      titulo: "Fee",
      render: (i: FeeGenerado) => (
        <Row espacio={2}>
          <span>{formatMoneda(i.fee, i.moneda)}</span>
          <Badge tono="neutro">{i.tipo}</Badge>
        </Row>
      ),
    },
    {
      clave: "estado",
      titulo: "Estado",
      render: (i: FeeGenerado) => {
        const detalle = textoEstadoFee(i);
        return (
          <Stack espacio={1}>
            <span>
              <Badge tono={TONO_ESTADO_FEE[i.estado]}>{ETIQUETA_ESTADO_FEE[i.estado]}</Badge>
            </span>
            {detalle && <span className="text-chico text-texto-suave">{detalle}</span>}
            {i.liquidaciones.map((l) => (
              <span key={l.id}>
                <Button variante="fantasma" tamanio="sm" onClick={() => t.abrirFactura(l)}>
                  {`Ver factura ${l.factura_nro}`}
                </Button>
              </span>
            ))}
          </Stack>
        );
      },
    },
  ];

  const columnasElegidos = [
    { clave: "fecha", titulo: "Fecha", render: (i: FeeGenerado) => formatFecha(i.fecha) },
    { clave: "gasto", titulo: "Gasto", render: (i: FeeGenerado) => [i.proveedor, i.descripcion].filter(Boolean).join(" · ") },
    { clave: "fee", titulo: "Fee", render: (i: FeeGenerado) => formatMoneda(i.fee, i.moneda) },
    { clave: "cobrar", titulo: "A cobrar", render: (i: FeeGenerado) => formatMoneda(i.por_cobrar, i.moneda) },
  ];

  const liqFactura = t.liquidacionFactura;

  return (
    <Stack espacio={6}>
      <h1 className="text-pantalla font-light tracking-[-0.03em]">Honorarios</h1>
      <p className="text-chico text-texto-suave">
        Lo que gana la fiduciaria{h ? ` (el ${formatNumero(h.fee_porcentaje)} % de cada gasto)` : ""}. El fee se genera entero con el pago que termina de pagarle
        al proveedor: el A queda por cobrar hasta que lo liquidás con una factura; el B se acredita en ese momento en la cuenta corriente de los que lo reciben.
      </p>
      <Alerta texto={t.aviso} tono="alerta" />

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}

      {h && (
        <>
          <Stack espacio={3}>
            <h2 className="text-titulo font-light tracking-tight">Fees generados</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <MetricCard titulo="Generado" valor={formatMoneda(t.totales.generado.usd, "USD")} detalle={textoTotales(t.totales.generado)} />
              <MetricCard titulo="Cobrado o acreditado" valor={formatMoneda(t.totales.cobrado.usd, "USD")} detalle={textoTotales(t.totales.cobrado)} tono="exito" />
              <MetricCard titulo="Por cobrar" valor={formatMoneda(t.totales.porCobrar.usd, "USD")} detalle={textoTotales(t.totales.porCobrar)} tono="alerta" />
            </div>
            <Row envolver espacio={3} alinear="fin">
              <TextField etiqueta="Buscar" placeholder="Gasto, comprobante, proveedor o factura" value={t.filtros.q} onChange={(e) => t.cambiarFiltro("q", e.target.value)} contenedorClassName="min-w-56 flex-1" />
              <Select etiqueta="Mes" opciones={t.opcionesMes} valor={t.filtros.mes} onCambio={(v) => t.cambiarFiltro("mes", v)} className="min-w-44" />
              <Select etiqueta="Tipo" opciones={OPCIONES_FILTRO_TIPO_FEE} valor={t.filtros.tipo} onCambio={(v) => t.cambiarFiltro("tipo", v)} className="w-28" />
              <Select etiqueta="Estado" opciones={OPCIONES_FILTRO_ESTADO_FEE} valor={t.filtros.estado} onCambio={(v) => t.cambiarFiltro("estado", v)} className="min-w-40" />
              <Select etiqueta="Proveedor" opciones={t.opcionesProveedor} valor={t.filtros.proveedorId} onCambio={(v) => t.cambiarFiltro("proveedorId", v)} className="min-w-48" />
              {t.hayFiltros && (
                <Button variante="fantasma" onClick={t.limpiarFiltros}>
                  Limpiar filtros
                </Button>
              )}
            </Row>
            {t.elegidos.length > 0 && (
              <Row justificar="entre" envolver espacio={3}>
                <span className="text-chico text-texto-suave">{`${t.elegidos.length} ${t.elegidos.length === 1 ? "fee elegido" : "fees elegidos"} · ${formatMoneda(t.totalElegidos, t.monedaElegidos)}`}</span>
                <Button onClick={t.abrirLiquidar}>Liquidar los elegidos</Button>
              </Row>
            )}
            {t.cargandoRegistro && (
              <div className="grid place-items-center py-6 text-primario">
                <Spinner />
              </div>
            )}
            <Alerta texto={t.errorRegistro} />
            {!t.cargandoRegistro && !t.errorRegistro && (
              <DataList etiqueta="Fees generados" columnas={columnasFees} filas={t.fees} claveFila={(i) => i.fee_id} vacio="No hay fees generados con estos filtros. El fee se genera cuando se termina de pagar el gasto." />
            )}
            <p className="text-chico text-texto-suave">Elegí los fees A por cobrar (de una misma moneda) y liquidalos juntos con la factura de la fiduciaria.</p>
          </Stack>

          <Stack espacio={3}>
            <h2 className="text-titulo font-light tracking-tight">Quiénes reciben el fee B</h2>
            <p className="text-chico text-texto-suave">Cuando se termina de pagar un gasto B, les acredita su parte del fee como un aporte. Al guardar se rearman los créditos de todo lo ya pagado.</p>
            {h.beneficiarios.length === 0 && t.lineas.length === 0 && (
              <Alerta tono="alerta" texto="Todavía nadie recibe el fee B: queda pendiente hasta que elijas a quiénes se acredita." />
            )}
            {t.lineas.length > 0 && (
              <DataList
                etiqueta="Quiénes reciben el fee B"
                columnas={columnasB}
                filas={t.lineas}
                claveFila={(l) => l.fiduciante_id}
                acciones={(l) => (
                  <Button variante="fantasma" tamanio="sm" onClick={() => t.quitar(l.fiduciante_id)}>
                    Quitar
                  </Button>
                )}
              />
            )}
            <Combobox etiqueta="Agregar fiduciante" opciones={t.opcionesBeneficiario} onElegir={t.agregar} placeholder="Escribí el nombre" />
            <Alerta texto={t.errorLineas ?? t.errorBeneficiarios} />
            {t.cambiaron && (
              <Row justificar="fin" espacio={2}>
                <Button variante="secundario" onClick={t.descartarBeneficiarios}>
                  Descartar
                </Button>
                <Button cargando={t.guardandoBeneficiarios} disabled={t.errorLineas !== null} onClick={() => void t.guardarBeneficiarios()}>
                  Guardar
                </Button>
              </Row>
            )}
          </Stack>

          <Stack espacio={3}>
            <h2 className="text-titulo font-light tracking-tight">Liquidaciones del fee A</h2>
            <DataList
              etiqueta="Liquidaciones del fee A"
              columnas={columnasLiq}
              filas={h.liquidaciones}
              claveFila={(l) => l.id}
              vacio="Todavía no hay liquidaciones."
              acciones={(l) => (
                <Row espacio={1}>
                  <Button variante="fantasma" tamanio="sm" onClick={() => t.abrirFactura(l)}>
                    {l.adjuntos.length > 0 ? `Factura (${l.adjuntos.length})` : "Subir factura"}
                  </Button>
                  <Button variante="fantasma" tamanio="sm" onClick={() => t.abrirAnular(l)}>
                    Anular
                  </Button>
                </Row>
              )}
            />
          </Stack>
        </>
      )}

      <Dialog abierto={t.dialogo?.tipo === "liquidar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={`Liquidar fees A en ${NOMBRE_MONEDA[t.form.moneda]}`} ancho="amplio">
        <form onSubmit={(e) => void t.liquidar(e)} noValidate>
          <Stack espacio={4}>
            <DataList etiqueta="Fees a liquidar" columnas={columnasElegidos} filas={t.elegidos} claveFila={(i) => i.fee_id} />
            <p>
              Total de la factura: <strong>{formatMoneda(t.totalElegidos, t.form.moneda)}</strong>
            </p>
            <TextField etiqueta="Número de factura" value={t.form.factura_nro} onChange={(e) => t.cambiar("factura_nro", e.target.value)} error={t.errores.factura_nro} />
            <TextField etiqueta="Fecha de la transferencia" type="date" value={t.form.fecha} onChange={(e) => t.cambiar("fecha", e.target.value)} error={t.errores.fecha} />
            {t.form.moneda === "ARS" && (
              <TextField etiqueta="Tipo de cambio" inputMode="decimal" value={t.form.tipo_cambio} onChange={(e) => t.cambiar("tipo_cambio", e.target.value)} error={t.errores.tipo_cambio} ayuda="Pesos por dólar del día" />
            )}
            {t.opcionesCaja && <Select etiqueta="Caja" opciones={t.opcionesCaja} valor={t.form.caja_id} onCambio={(v) => t.cambiar("caja_id", v)} placeholder="Elegí la caja" />}
            <Stack espacio={1}>
              <SelectorArchivos etiqueta="Factura en PDF" textoBoton={t.factura ? "Cambiar la factura" : "Adjuntar la factura"} accept={ACCEPT_ADJUNTO} onElegir={t.elegirFactura} />
              {t.factura && <p className="text-chico text-texto-suave">{t.factura.name}</p>}
              <Alerta texto={t.errorFactura} />
            </Stack>
            <Alerta texto={t.errorLiquidar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.liquidando} disabled={t.elegidos.length === 0}>
                Liquidar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={t.dialogo?.tipo === "anular"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Anular la liquidación">
        <Stack espacio={4}>
          {t.dialogo?.tipo === "anular" && (
            <p>
              ¿Anular la liquidación de {nombreMes(t.dialogo.liquidacion.mes)} en {NOMBRE_MONEDA[t.dialogo.liquidacion.moneda]} (factura {t.dialogo.liquidacion.factura_nro})? Sus fees vuelven
              a quedar por cobrar, sale de la caja y se borra la factura adjunta.
            </p>
          )}
          <Alerta texto={t.errorAnular} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.anulando} onClick={() => void t.confirmarAnular()}>
              Anular
            </Button>
          </Row>
        </Stack>
      </Dialog>

      <Dialog abierto={t.dialogo?.tipo === "factura"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={liqFactura ? `Factura ${liqFactura.factura_nro}` : "Factura"}>
        {liqFactura && (
          <Adjuntos
            etiqueta={`Factura ${liqFactura.factura_nro}`}
            adjuntos={liqFactura.adjuntos}
            rutaDescarga={t.rutaDescarga}
            puedeEditar
            onSubir={t.subirFactura}
            onBorrar={t.borrarFactura}
            subiendo={t.subiendo}
            error={t.errorAdjuntos}
          />
        )}
      </Dialog>
    </Stack>
  );
}
